<template>
  <div class="category-management">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span>分类管理</span>
        <el-button style="float: right; padding: 3px 0" type="text" @click="handleAdd">新增分类</el-button>
      </div>

      <!-- Level Tabs -->
      <el-tabs v-model="activeLevel" @tab-click="handleLevelChange">
        <el-tab-pane label="一级分类" name="1"></el-tab-pane>
        <el-tab-pane label="二级分类" name="2"></el-tab-pane>
        <el-tab-pane label="三级分类" name="3"></el-tab-pane>
      </el-tabs>

      <!-- Parent Category Selector for Level 2 & 3 -->
      <div v-if="activeLevel !== '1'" class="parent-selector">
        <el-select v-model="selectedParentId" placeholder="请选择上级分类" @change="loadCategories">
          <el-option
            v-for="parent in parentCategories"
            :key="parent.id"
            :label="parent.title"
            :value="parent.id">
          </el-option>
        </el-select>
      </div>

      <!-- Category Table -->
      <el-table
        :data="categories"
        style="width: 100%"
        v-loading="loading">
        <el-table-column prop="id" label="ID" width="80"></el-table-column>
        <el-table-column prop="title" label="标题"></el-table-column>
        <el-table-column prop="description" label="描述" show-overflow-tooltip></el-table-column>
        <el-table-column label="媒体" width="120">
          <template slot-scope="scope">
            <div v-if="scope.row.mediaUrl" class="media-preview">
              <img 
                v-if="scope.row.mediaType === 1" 
                :src="scope.row.mediaUrl" 
                class="media-img"
                @click="previewMedia(scope.row.mediaUrl, 1)">
              <video 
                v-else 
                :src="scope.row.mediaUrl" 
                class="media-video"
                @click="previewMedia(scope.row.mediaUrl, 2)">
              </video>
            </div>
            <span v-else>无</span>
          </template>
        </el-table-column>
        <el-table-column prop="sort" label="排序" width="80"></el-table-column>
        <el-table-column label="操作" width="200">
          <template slot-scope="scope">
            <el-button size="mini" @click="handleEdit(scope.row)">编辑</el-button>
            <el-button size="mini" type="danger" @click="handleDelete(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>

    <!-- Add/Edit Dialog -->
    <el-dialog :title="dialogTitle" :visible.sync="dialogVisible" width="600px">
      <el-form :model="form" :rules="rules" ref="form" label-width="100px">
        <el-form-item label="分类标题" prop="title">
          <el-input v-model="form.title"></el-input>
        </el-form-item>
        <el-form-item label="分类描述" prop="description">
          <el-input type="textarea" v-model="form.description" rows="3"></el-input>
        </el-form-item>
        <el-form-item v-if="form.level !== 1" label="上级分类" prop="parent_id">
          <el-select v-model="form.parent_id" placeholder="请选择上级分类" style="width: 100%">
            <el-option
              v-for="parent in dialogParentCategories"
              :key="parent.id"
              :label="parent.title"
              :value="parent.id">
            </el-option>
          </el-select>
        </el-form-item>
        <el-form-item label="媒体类型" prop="media_type">
          <el-radio-group v-model="form.media_type">
            <el-radio :label="1">图片</el-radio>
            <el-radio :label="2">视频</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="媒体链接" prop="media_url">
          <el-input 
            v-model="form.media_url" 
            placeholder="可以直接填写图片或视频的链接地址（支持 http/https 链接）"
            style="margin-bottom: 10px;">
            <template slot="prepend">
              <i class="el-icon-link"></i>
            </template>
          </el-input>
          <div class="upload-divider">
            <span class="divider-text">或</span>
          </div>
          <el-upload
            action="#"
            :http-request="handleCustomUpload"
            :before-upload="beforeUpload"
            :show-file-list="false">
            <el-button size="small" type="primary">点击上传文件</el-button>
            <div slot="tip" class="el-upload__tip">
              支持 jpg/png 图片或 mp4 视频，文件大小不超过 10MB（大视频建议直接填写链接，速度更快）
            </div>
          </el-upload>
          <div v-if="form.media_url" class="uploaded-media">
            <img 
              v-if="form.media_type === 1" 
              :src="form.media_url" 
              class="preview-img"
              @error="handleMediaError">
            <video 
              v-else 
              :src="form.media_url" 
              class="preview-video"
              controls
              @error="handleMediaError">
            </video>
          </div>
        </el-form-item>
        <el-form-item label="排序" prop="sort">
          <el-input-number v-model="form.sort" :min="0"></el-input-number>
        </el-form-item>
      </el-form>
      <div slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmit">确定</el-button>
      </div>
    </el-dialog>

    <!-- Media Preview Dialog -->
    <el-dialog title="媒体预览" :visible.sync="previewVisible" width="800px">
      <div class="media-container">
        <img v-if="previewType === 1" :src="previewUrl" class="preview-full-img">
        <video v-else :src="previewUrl" class="preview-full-video" controls></video>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { categoryApi, uploadApi } from '@/api'

export default {
  name: 'CategoryManagement',
  data() {
    return {
      activeLevel: '1',
      categories: [],
      parentCategories: [],
      dialogParentCategories: [], // 对话框中的上级分类列表
      selectedParentId: null,
      loading: false,
      dialogVisible: false,
      previewVisible: false,
      dialogTitle: '新增分类',
      previewUrl: '',
      previewType: 1,
      form: {
        id: null,
        title: '',
        description: '',
        media_url: '',
        media_type: 1,
        level: 1,
        parent_id: 0,
        sort: 0
      },
      rules: {
        title: [
          { required: true, message: '请输入分类标题', trigger: 'blur' },
          { max: 100, message: '标题长度不能超过100个字符', trigger: 'blur' }
        ],
        media_type: [
          { required: true, message: '请选择媒体类型', trigger: 'change' }
        ]
      }
    }
  },
  watch: {
    activeLevel: {
      handler(newLevel) {
        this.form.level = parseInt(newLevel)
        if (newLevel === '1') {
          this.form.parent_id = 0
          this.selectedParentId = null
        } else {
          this.loadParentCategories()
        }
      },
      immediate: true
    }
  },
  mounted() {
    this.loadCategories()
  },
  methods: {
    async loadCategories() {
      this.loading = true
      try {
        const params = { level: this.activeLevel }
        if (this.activeLevel !== '1' && this.selectedParentId) {
          params.parent_id = this.selectedParentId
        }
        const response = await categoryApi.getAll(params)
        this.categories = Array.isArray(response) ? response.sort((a, b) => a.sort - b.sort) : []
      } catch (error) {
        this.$message.error('加载分类失败: ' + error.message)
      } finally {
        this.loading = false
      }
    },
    async loadParentCategories() {
      try {
        const parentLevel = this.activeLevel === '2' ? 1 : 2
        const response = await categoryApi.getAll({ level: parentLevel })
        this.parentCategories = Array.isArray(response) ? response.sort((a, b) => a.sort - b.sort) : []
      } catch (error) {
        this.$message.error('加载上级分类失败: ' + error.message)
      }
    },
    async loadDialogParentCategories(level) {
      // 加载对话框中使用的上级分类列表
      if (level === 1) {
        this.dialogParentCategories = []
        return
      }
      try {
        const parentLevel = level === 2 ? 1 : 2
        const response = await categoryApi.getAll({ level: parentLevel })
        this.dialogParentCategories = Array.isArray(response) ? response.sort((a, b) => a.sort - b.sort) : []
      } catch (error) {
        this.$message.error('加载上级分类失败: ' + error.message)
        this.dialogParentCategories = []
      }
    },
    handleLevelChange() {
      this.loadCategories()
    },
    async handleAdd() {
      this.dialogTitle = '新增分类'
      const level = parseInt(this.activeLevel)
      // 加载对应的上级分类列表
      await this.loadDialogParentCategories(level)
      this.form = {
        id: null,
        title: '',
        description: '',
        media_url: '',
        media_type: 1,
        level: level,
        parent_id: level === 1 ? 0 : (this.selectedParentId || (this.dialogParentCategories.length > 0 ? this.dialogParentCategories[0].id : 0)),
        sort: 0
      }
      this.dialogVisible = true
    },
    async handleEdit(row) {
      this.dialogTitle = '编辑分类'
      // 加载对应的上级分类列表
      await this.loadDialogParentCategories(row.level)
      // 接口返回的是驼峰格式（mediaType, mediaUrl, parentId），需要转换为表单使用的下划线格式
      this.form = {
        id: row.id,
        title: row.title,
        description: row.description || '',
        media_url: row.mediaUrl || row.media_url || '',
        // 接口返回 mediaType，确保正确获取（1=图片，2=视频）
        media_type: (row.mediaType !== undefined && row.mediaType !== null) ? row.mediaType : (row.media_type || 1),
        level: row.level,
        parent_id: (row.parentId !== undefined && row.parentId !== null) ? row.parentId : (row.parent_id || 0),
        sort: row.sort || 0
      }
      this.dialogVisible = true
    },
    async handleDelete(row) {
      try {
        await this.$confirm('确定要删除这个分类吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        await categoryApi.delete(row.id)
        this.$message.success('删除成功')
        this.loadCategories()
      } catch (error) {
        if (error !== 'cancel') {
          this.$message.error('删除失败: ' + error.message)
        }
      }
    },
    async handleSubmit() {
      try {
        await this.$refs.form.validate()
        // 将表单的下划线格式转换为接口需要的驼峰格式
        const submitData = {
          title: this.form.title,
          description: this.form.description,
          mediaUrl: this.form.media_url,
          mediaType: this.form.media_type,
          level: this.form.level,
          // 一级分类的 parentId 必须是 0
          parentId: this.form.level === 1 ? 0 : (this.form.parent_id || 0),
          sort: this.form.sort
        }
        if (this.form.id) {
          await categoryApi.update(this.form.id, submitData)
          this.$message.success('更新成功')
        } else {
          await categoryApi.create(submitData)
          this.$message.success('创建成功')
        }
        this.dialogVisible = false
        this.loadCategories()
      } catch (error) {
        this.$message.error('操作失败: ' + error.message)
      }
    },
    beforeUpload(file) {
      const isImage = file.type.startsWith('image/')
      const isVideo = file.type.startsWith('video/')
      const isValidType = isImage || isVideo
      const isLt10M = file.size / 1024 / 1024 < 10

      if (!isValidType) {
        this.$message.error('只能上传图片或视频文件!')
        return false
      }
      if (!isLt10M) {
        this.$message.error('文件大小不能超过 10MB!')
        return false
      }
      
      if (isImage) {
        this.form.media_type = 1
      } else {
        this.form.media_type = 2
      }
      
      return true
    },
    async handleCustomUpload(options) {
      try {
        const response = await uploadApi.uploadFile(options.file, (progress) => {
          // 可以在这里显示上传进度
          console.log('上传进度:', progress + '%')
        })
        
        // uploadApi 返回的是经过拦截器处理后的 data.data
        // 如果后端返回 { code: 200, data: "图片URL" }，这里 response 就是 "图片URL"
        // 如果后端返回 { code: 200, data: { url: "图片URL" } }，这里 response 就是 { url: "图片URL" }
        if (typeof response === 'string') {
          this.form.media_url = response
        } else if (response && response.url) {
          this.form.media_url = response.url
        } else if (response && response.data) {
          this.form.media_url = typeof response.data === 'string' ? response.data : response.data.url
        } else {
          this.form.media_url = response
        }
        
        this.$message.success('上传成功')
      } catch (error) {
        this.$message.error('上传失败: ' + (error.message || '未知错误'))
      }
    },
    previewMedia(url, type) {
      this.previewUrl = url
      this.previewType = type
      this.previewVisible = true
    },
    handleMediaError() {
      // 媒体加载失败时的处理（静默处理，不显示错误）
      // 可能是链接无效，但不影响用户继续操作
    }
  }
}
</script>

<style scoped>
.category-management {
  padding: 20px;
}

.parent-selector {
  margin: 20px 0;
}

.media-preview {
  width: 60px;
  height: 60px;
  cursor: pointer;
}

.media-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.media-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 4px;
}

.upload-divider {
  margin: 15px 0;
  text-align: center;
  position: relative;
}

.upload-divider::before,
.upload-divider::after {
  content: '';
  position: absolute;
  top: 50%;
  width: 40%;
  height: 1px;
  background-color: #dcdfe6;
}

.upload-divider::before {
  left: 0;
}

.upload-divider::after {
  right: 0;
}

.divider-text {
  position: relative;
  padding: 0 10px;
  background-color: #fff;
  color: #909399;
  font-size: 12px;
}

.uploaded-media {
  margin-top: 10px;
}

.preview-img {
  width: 200px;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
}

.preview-video {
  width: 200px;
  height: 150px;
  object-fit: cover;
  border-radius: 4px;
}

.media-container {
  text-align: center;
}

.preview-full-img {
  max-width: 100%;
  max-height: 500px;
}

.preview-full-video {
  max-width: 100%;
  max-height: 500px;
}
</style>
